/**
 * ProductTour — spotlight + tooltip portal for onboarding steps.
 *
 * Prefer driving tours via OnboardingProvider. useProductTour remains for
 * lightweight page-local tours that do not need cross-route navigation.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  getTourState,
  isTourFinished,
  markTourCompleted,
  markTourSkipped,
  resetTourCompletion,
  setLastStepIndex,
  isTourCompleted,
} from "./tourStorage";

export {
  isTourCompleted,
  isTourFinished,
  markTourCompleted,
  markTourSkipped,
  resetTourCompletion,
  getTourState,
  setLastStepIndex,
};

function isVisibleTarget(el) {
  if (!el || !(el instanceof Element)) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return false;
  }
  const r = el.getBoundingClientRect();
  return r.width >= 2 && r.height >= 2;
}

function resolveTarget(step) {
  if (!step) return null;
  if (step.ref?.current && isVisibleTarget(step.ref.current)) return step.ref.current;
  if (step.selector) {
    try {
      const nodes = document.querySelectorAll(step.selector);
      for (const el of nodes) {
        if (isVisibleTarget(el)) return el;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Hook: page-local tour state (no cross-route). Prefer useOnboarding for app tours.
 */
export function useProductTour({
  tourId,
  steps = [],
  autoStart = false,
  onComplete,
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const start = useCallback(() => {
    setStepIndex(0);
    setIsOpen(true);
  }, []);

  const stop = useCallback(
    (mode = "close") => {
      setIsOpen(false);
      if (!tourId) return;
      if (mode === "finish") {
        markTourCompleted(tourId);
        onComplete?.();
      } else if (mode === "skip") {
        markTourSkipped(tourId, stepIndex);
      }
    },
    [tourId, onComplete, stepIndex]
  );

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= steps.length - 1) {
        stop("finish");
        return i;
      }
      const nextIdx = i + 1;
      if (tourId) setLastStepIndex(tourId, nextIdx);
      return nextIdx;
    });
  }, [steps.length, stop, tourId]);

  const prev = useCallback(() => {
    setStepIndex((i) => {
      const nextIdx = Math.max(0, i - 1);
      if (tourId) setLastStepIndex(tourId, nextIdx);
      return nextIdx;
    });
  }, [tourId]);

  useEffect(() => {
    if (!autoStart || !tourId || steps.length === 0) return;
    if (isTourFinished(tourId)) return;
    const t = window.setTimeout(() => start(), 400);
    return () => window.clearTimeout(t);
  }, [autoStart, tourId, steps.length, start]);

  return {
    isOpen,
    stepIndex,
    step: steps[stepIndex] || null,
    steps,
    isFirst: stepIndex === 0,
    isLast: stepIndex >= steps.length - 1,
    start,
    stop: () => stop("close"),
    finish: () => stop("finish"),
    skip: () => stop("skip"),
    next,
    prev,
    setStepIndex,
  };
}

/**
 * Spotlight + tooltip portal. Render when tour.isOpen.
 */
export function ProductTour({
  isOpen,
  steps,
  stepIndex,
  onNext,
  onPrev,
  onFinish,
  onSkip,
  onClose,
  primaryLabel,
  targetReady = true,
  mandatory = false,
}) {
  const step = steps[stepIndex];
  const tooltipRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [tipPos, setTipPos] = useState({ top: 80, left: 24 });

  const isCentered = !step?.selector && !step?.ref && (step?.placement === "center" || !step?.placement);

  const measure = useCallback(() => {
    if (!step || isCentered) {
      setRect(null);
      return;
    }
    const el = resolveTarget(step);
    if (!el) {
      setRect(null);
      return;
    }
    // Instant scroll first, then measure — smooth scroll caused spotlight mismatch.
    el.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) {
      setRect(null);
      return;
    }
    setRect({
      top: r.top + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      height: r.height,
      vwTop: r.top,
      vwLeft: r.left,
    });
  }, [step, isCentered]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    measure();
    // Remeasure after layout settles (fonts, panel open, async content).
    const t1 = window.setTimeout(measure, 50);
    const t2 = window.setTimeout(measure, 250);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [isOpen, stepIndex, measure, targetReady]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const tip = tooltipRef.current;
    const tipW = tip?.offsetWidth || 320;
    const tipH = tip?.offsetHeight || 160;
    const gap = 12;

    if (isCentered || !rect) {
      setTipPos({
        top: Math.max(24, (window.innerHeight - tipH) / 2),
        left: Math.max(12, (window.innerWidth - tipW) / 2),
      });
      return;
    }

    const placement = step?.placement || "bottom";
    let top = rect.vwTop + rect.height + gap;
    let left = rect.vwLeft;

    if (placement === "top") top = rect.vwTop - tipH - gap;
    if (placement === "left") {
      top = rect.vwTop;
      left = rect.vwLeft - tipW - gap;
    }
    if (placement === "right") {
      top = rect.vwTop;
      left = rect.vwLeft + rect.width + gap;
    }

    left = clamp(left, 12, window.innerWidth - tipW - 12);
    top = clamp(top, 12, window.innerHeight - tipH - 12);
    setTipPos({ top, left });
  }, [isOpen, rect, step, isCentered]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === "Escape" && !mandatory) onClose?.();
      if (e.key === "ArrowRight" || e.key === "Enter") onNext?.();
      if (e.key === "ArrowLeft") onPrev?.();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onNext, onPrev, onClose, mandatory]);

  const isLast = stepIndex >= steps.length - 1;
  const isFirst = stepIndex <= 0;

  const pad = 8;
  const hole = useMemo(() => {
    if (!rect || isCentered) return null;
    return {
      top: Math.max(0, rect.vwTop - pad),
      left: Math.max(0, rect.vwLeft - pad),
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    };
  }, [rect, isCentered]);

  if (!isOpen || !step) return null;

  const waiting = Boolean(step.selector || step.ref) && !isCentered && !hole && !targetReady;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-tour-title"
      aria-describedby="product-tour-desc"
    >
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={mandatory ? undefined : onClose}
        aria-hidden
      >
        {hole ? (
          <div
            className="rounded-xl transition-all duration-200 ease-out ring-2 ring-brand-blue ring-offset-2 dark:ring-offset-neutral-bg2"
            style={{
              position: "fixed",
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              boxShadow: "0 0 0 9999px rgba(10, 30, 80, 0.55)",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-brand-navy/55 dark:bg-black/60" />
        )}
      </div>

      <div
        ref={tooltipRef}
        className="fixed z-[1001] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-bg3 p-4 shadow-lg"
        style={{ top: tipPos.top, left: tipPos.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-text-muted">
            Step {stepIndex + 1} of {steps.length}
          </p>
          {!mandatory && (
            <button
              type="button"
              onClick={onClose}
              className="min-h-9 min-w-9 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
              aria-label="Close tour"
            >
              ×
            </button>
          )}
        </div>
        <h2
          id="product-tour-title"
          className="text-lg font-semibold text-brand-navy dark:text-white leading-snug"
        >
          {step.title}
        </h2>
        <p
          id="product-tour-desc"
          className="mt-2 text-sm text-neutral-600 dark:text-text-secondary leading-relaxed"
        >
          {step.description}
        </p>
        {waiting && (
          <p className="mt-2 text-xs text-neutral-400 dark:text-text-muted">
            Loading this area…
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={isFirst}
            className="min-h-11 rounded-lg border border-neutral-300 dark:border-white/15 px-4 text-sm font-medium text-brand-navy dark:text-white disabled:opacity-40 hover:bg-neutral-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          >
            Previous
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={onFinish}
              className="min-h-11 rounded-lg bg-brand-blue px-4 text-sm font-medium text-white hover:bg-brand-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            >
              {primaryLabel || "Finish"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="min-h-11 rounded-lg bg-brand-blue px-4 text-sm font-medium text-white hover:bg-brand-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            >
              Next
            </button>
          )}
          {!mandatory && (
            <button
              type="button"
              onClick={onSkip || onFinish}
              className="ml-auto text-xs font-medium text-neutral-500 dark:text-text-muted hover:text-brand-navy dark:hover:text-white underline-offset-2 hover:underline"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
